
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext'; // Importar useAuth

export type AITaskType = 
  | 'case_analysis' 
  | 'document_generation' 
  | 'deadline_management' 
  | 'legal_research' 
  | 'general'
  | 'document_summarization';

export interface AIRequest {
  prompt: string;
  context?: string;
  task_type: AITaskType;
}

export const useAI = () => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth(); // Obter o usuário logado


  const askAI = async ({ prompt, context, task_type }: AIRequest): Promise<{ response: string | null; interactionId: string | null } | null> => {
    setLoading(true);
    
    let interactionSuccess = false;
    let interactionErrorMessage: string | undefined;
    let aiResponseContent: string | null = null;
    let interactionId: string | null = null;

    try {
      const { data, error } = await supabase.functions.invoke('ai-assistant', {
        body: { prompt, context, task_type }
      });

      if (error) {
        console.error('Supabase function error:', error);
        throw new Error(`Erro na função: ${error.message}`);
      }
      
      if (!data) {
        throw new Error('Nenhuma resposta recebida da IA');
      }

      if (!data.success) {
        const errorMsg = data.error || 'Erro desconhecido na resposta da IA';
        console.error('AI response error:', errorMsg);
        throw new Error(errorMsg);
      }

      if (!data.response) {
        throw new Error('Resposta vazia da IA');
      }

      aiResponseContent = data.response;
      interactionSuccess = true;

      // Record successful interaction here
      if (user) {
        const { data: interactionData, error: insertError } = await supabase.from('ai_interactions').insert({
          user_id: user.id,
          task_type: task_type,
          prompt_length: prompt.length,
          response_length: aiResponseContent?.length || 0,
          success: interactionSuccess,
          error_message: interactionErrorMessage,
          prompt_content: prompt,
          response_content: aiResponseContent
        }).select('id').single();

        if (insertError) {
          console.error("Erro ao registrar interação da IA:", insertError);
        } else {
          interactionId = interactionData?.id || null;
        }
      }
      
      setLoading(false); // Set loading to false on success
      return { response: aiResponseContent, interactionId: interactionId };

    } catch (error) {
      console.error('AI Error:', error);
      
      let errorMessage = "Não foi possível processar sua solicitação.";
      
      if (error instanceof Error) {
        if (error.message.includes('OpenAI')) {
          errorMessage = "Erro na chave OpenAI. Verifique sua configuração.";
        } else if (error.message.includes('Network')) {
          errorMessage = "Erro de conexão. Tente novamente.";
        } else {
          errorMessage = error.message;
        }
        interactionErrorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
        interactionErrorMessage = error;
      }
      
      toast({
        title: "Erro na IA",
        description: errorMessage,
        variant: "destructive"
      });
      interactionSuccess = false;
      aiResponseContent = null; // Ensure response content is null on error

      // Record failed interaction here
      if (user) {
        try {
          const { data: interactionData, error: insertError } = await supabase.from('ai_interactions').insert({
            user_id: user.id,
            task_type: task_type,
            prompt_length: prompt.length,
            response_length: aiResponseContent?.length || 0, // Will be 0 for failed
            success: interactionSuccess,
            error_message: interactionErrorMessage,
            prompt_content: prompt,
            response_content: aiResponseContent // Will be null for failed
          }).select('id').single();
  
          if (insertError) {
            console.error("Erro ao registrar interação da IA:", insertError);
          } else {
            interactionId = interactionData?.id || null;
          }
        } catch (dbError) {
          console.error("Erro ao registrar interação da IA no banco de dados:", dbError);
        }
      }
      setLoading(false); // Set loading to false on error
      return { response: null, interactionId: interactionId };
    }
  };

  const analyzeCase = async (caseData: any) => {
    const context = `
      Processo: ${caseData.title}
      Tipo: ${caseData.case_type}
      Status: ${caseData.status}
      Prioridade: ${caseData.priority}
      Prazo: ${caseData.deadline}
      Descrição: ${caseData.description}
      Cliente: ${caseData.client_name}
    `;

    const result = await askAI({
      prompt: "Analise este processo jurídico e forneça insights detalhados.",
      context,
      task_type: 'case_analysis'
    });
    return result?.response || null;
  };

  const generateDocument = async (documentType: string, caseInfo: any) => {
    const context = `
      Tipo de documento: ${documentType}
      Processo: ${caseInfo.title}
      Cliente: ${caseInfo.client_name}
      Detalhes: ${caseInfo.description}
    `;

    const result = await askAI({
      prompt: `Gere um ${documentType} para este processo.`,
      context,
      task_type: 'document_generation'
    });
    return result?.response || null;
  };

  const checkDeadlines = async (cases: any[]) => {
    const context = cases.map(c => `${c.title}: ${c.deadline} (${c.status})`).join('\n');

    const result = await askAI({
      prompt: "Analise estes prazos e identifique urgências e próximas ações necessárias.",
      context,
      task_type: 'deadline_management'
    });
    return result?.response || null;
  };

  const summarizeDocument = async (documentContent: string) => {
    const result = await askAI({
      prompt: "Resuma o seguinte documento, destacando os pontos mais importantes e as informações chave.",
      context: documentContent,
      task_type: 'document_summarization'
    });
    return result?.response || null;
  };

  return {
    loading,
    askAI,
    analyzeCase,
    generateDocument,
    checkDeadlines,
    summarizeDocument
  };
};
